/**
 * alignRomanToNative — map a romanised (pinyin) tiling onto native-script
 * (hanzi) text so a SINGLE decomposition can be shown in either script.
 *
 * Why this exists
 * ───────────────
 * Pinyin is whitespace-segmented into words and chunks cleanly into the
 * 2-3-unit tiles the player wants. Hanzi has no spaces, so re-segmenting the
 * native text on its own collapses whole phrases into one giant tile. But
 * Mandarin has a property we can exploit: **one hanzi character = one
 * spoken syllable**. So if we know how many syllables each pinyin tile holds,
 * we can slice exactly that many hanzi characters off the native phrase and
 * hand each tile its matching characters — no native segmentation needed.
 *
 * Measured against the live zho_for_eng course this aligns 399/400 phrases;
 * the one residual is a row with no romanisation at all (a content gap), which
 * the caller falls back on. The previously-failing cases were all erhua
 * (一会儿 "yíhuìr"), handled below.
 *
 * Scope: Mandarin-specific (syllable counting is pinyin-shaped). Japanese
 * romaji↔kanji is NOT 1-mora-1-char, so this must not be used for Japanese —
 * the caller relies on lego-level pairing there instead.
 */

import type { LegoBlock } from '../components/LegoAssembly.vue'

/** Hanzi (CJK unified ideographs incl. Ext-A) — the "one char = one syllable"
 *  units we slice. Kana/Hangul deliberately excluded: this is Mandarin-only. */
const HANZI_RE = /[㐀-鿿]/
/** Pinyin vowel nuclei, including every tone-marked variant. Each maximal run
 *  of these is one syllable nucleus. */
const VOWEL_RE = /[aeiouüvāáǎàēéěèīíǐìōóǒòūúǔùǖǘǚǜ]/i

/** Strip tone marks / lowercase and unify apostrophes so we can recognise the
 *  standalone "er" syllable (二/而/儿) and the pinyin syllable-boundary
 *  apostrophe. ü/v fold to u (a vowel either way for nucleus counting). */
function deaccent(word: string): string {
  return word
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // combining tone marks
    .replace(/ü/g, 'u')
    .replace(/v/g, 'u')
    .replace(/[‘’ʼ`´]/g, "'") // unify apostrophe variants
}

/**
 * Count Mandarin syllables (= hanzi characters) in ONE pinyin word token.
 *
 * Base count = number of maximal vowel runs (one vowel nucleus per syllable).
 * Plus erhua: a word-final 'r' that is NOT itself the "er" syllable adds one
 * character (the merged 儿 / 兒). The standalone "er" token (二/而/儿) is
 * guarded so its coda 'r' is not double-counted.
 */
const syllableCounts = new Map<string, number>()
const MAX_CACHED_PINYIN_WORDS = 1024

export function pinyinSyllableCount(word: string): number {
  // Tile rebuilds reuse course vocabulary. Cache only the pure count, never
  // mutable tiles, and bound retention as learners move between courses.
  const cached = syllableCounts.get(word)
  if (cached !== undefined) return cached
  const count = countPinyinSyllables(word)
  if (syllableCounts.size >= MAX_CACHED_PINYIN_WORDS) {
    syllableCounts.delete(syllableCounts.keys().next().value!)
  }
  syllableCounts.set(word, count)
  return count
}

function countPinyinSyllables(word: string): number {
  const norm = deaccent(word)
  const bare = norm.replace(/[^a-z]/g, '')
  if (!bare) return 0

  // Count maximal vowel runs. An apostrophe is pinyin's explicit syllable
  // boundary (dá'àn 答案, nǚ'ér 女儿) — it forces a run break so the two
  // syllables either side are counted separately rather than merged as one
  // vowel cluster. Consonants break runs the same way.
  let nuclei = 0
  let inRun = false
  for (const ch of norm) {
    if (/[a-z]/.test(ch) && VOWEL_RE.test(ch)) {
      if (!inRun) { nuclei++; inRun = true }
    } else {
      inRun = false
    }
  }
  if (nuclei === 0) return 0

  // Erhua (merged 儿 / 兒): a word-final 'r' that isn't a syllable-initial is
  // either the standalone "er" syllable or an erhua suffix. In pinyin the only
  // base final ending in 'r' is "er" itself, so any trailing 'r' is one of:
  //   • the whole word is "er"            (二/而)            — already counted
  //   • C + er  e.g. "gēr" (歌儿)          — 'e' run counts 歌, 'r' adds 儿
  //   • V + er  e.g. "nǚér" (女儿)         — "er" is its own counted syllable
  //   • <coda> + r e.g. "wánr"/"nàr"       — 'r' is erhua, adds one character
  if (bare !== 'er' && bare.endsWith('r')) {
    const beforeR = bare[bare.length - 2]
    if (beforeR === 'e') {
      // Distinguish a counted "er" syllable from a C+er erhua. The "er"
      // syllable has no initial — 'e' sits at word start or after another
      // vowel (a syllable boundary). 'e' after a consonant means C+er erhua.
      const beforeE = bare[bare.length - 3]
      if (beforeE !== undefined && !VOWEL_RE.test(beforeE)) nuclei += 1
    } else if (beforeR !== undefined) {
      nuclei += 1
    }
  }
  return nuclei
}

/** Whitespace-split a roman phrase/tile into pinyin word tokens. */
function pinyinWords(roman: string): string[] {
  return roman.trim().split(/\s+/).filter(Boolean)
}

/** Total syllables (= expected hanzi count) across a roman string. */
function syllablesIn(roman: string): number {
  // Word tiles are already whitespace-tokenized. Avoid splitting and allocating
  // another array per word; multi-word LEGO chunks still use the full path.
  if (!/\s/.test(roman)) return pinyinSyllableCount(roman)
  return pinyinWords(roman).reduce((sum, w) => sum + pinyinSyllableCount(w), 0)
}

/** Count hanzi characters in a native string. */
// Tile rebuilds can change romanisation or salience while retaining the native
// phrase. Keep only its last count; returned slices are always built afresh.
let lastHanziCount: { native: string; count: number } | undefined
function hanziCount(native: string): number {
  if (lastHanziCount?.native === native) return lastHanziCount.count
  let n = 0
  for (const ch of native) if (HANZI_RE.test(ch)) n++
  lastHanziCount = { native, count: n }
  return n
}

/**
 * Slice a native (hanzi) phrase into per-tile substrings parallel to an
 * ordered list of romanised tile texts.
 *
 * Each tile consumes `pinyinSyllableCount`-many hanzi characters from a running
 * cursor over `nativePhrase`; any non-hanzi characters (punctuation, the rare
 * latin token) that immediately follow are attached to that same tile, and any
 * leading non-hanzi is attached to the first tile.
 *
 * Returns `null` when the alignment can't be trusted — total syllables across
 * the tiles don't equal the hanzi count, or the phrase has no hanzi at all.
 * The caller then keeps the romanised tiling unchanged rather than risk a
 * mis-sliced native render.
 */
export function sliceNativeByRoman(
  romanTexts: string[],
  nativePhrase: string,
): string[] | null {
  if (!nativePhrase) return null
  const totalHan = hanziCount(nativePhrase)
  if (totalHan === 0) return null

  const wantPerTile = romanTexts.map(syllablesIn)
  const totalWant = wantPerTile.reduce((a, b) => a + b, 0)
  // Erhua + standalone-er handling should make these equal. If they diverge
  // the data is anomalous (e.g. a null/partial romanisation) — bail so the
  // caller falls back instead of producing a silently-shifted alignment.
  if (totalWant !== totalHan) return null

  // A validated whole-phrase tile owns every character, including punctuation.
  // Avoid allocating and walking a character array just to rebuild the input.
  if (romanTexts.length === 1) return [nativePhrase]

  const chars = [...nativePhrase]
  const out: string[] = []
  let cursor = 0

  // Leading non-hanzi (rare) rides on the first tile.
  let lead = ''
  while (cursor < chars.length && !HANZI_RE.test(chars[cursor])) {
    lead += chars[cursor]
    cursor++
  }

  for (let t = 0; t < romanTexts.length; t++) {
    // Counts already match, so after earlier tiles consume their share the
    // final tile owns the remainder (including trailing punctuation).
    if (t === romanTexts.length - 1) {
      out.push(chars.slice(cursor).join(''))
      cursor = chars.length
      break
    }
    let take = wantPerTile[t]
    let piece = ''
    while (cursor < chars.length) {
      const ch = chars[cursor]
      const isHan = HANZI_RE.test(ch)
      // Stop before a hanzi that belongs to the next tile.
      if (isHan && take === 0) break
      piece += ch
      if (isHan) take--
      cursor++
    }
    out.push(piece)
  }

  // Any trailing characters (shouldn't happen once counts match) join the last
  // tile so no native text is ever dropped.
  if (cursor < chars.length) {
    out[out.length - 1] = (out[out.length - 1] || '') + chars.slice(cursor).join('')
  }
  if (lead) out[0] = lead + (out[0] || '')

  return out
}

export interface WordTileOptions {
  /** Native-script text(s) of the salient LEGO(s). Word tiles whose characters
   *  fall inside one of these get isSalient=true (visually emphasised). When
   *  none are supplied / matched, every tile is salient (full emphasis) rather
   *  than fading the whole phrase. */
  salientNativeTexts?: string[]
  /** Stable id prefix for the generated tiles. */
  idPrefix?: string
}

/**
 * Tile a phrase by PINYIN WORD rather than by LEGO.
 *
 * A LEGO can be an entire clause (e.g. 你想什么时候开始 — "when do you want to
 * start"), which is unparseable as a single hanzi tile. Pinyin, by contrast, is
 * whitespace-segmented into 1-3-syllable words — exactly the chunking a learner
 * can read. So for Mandarin we build one tile per pinyin word, slice the native
 * phrase to match each word's syllable count, and carry the pinyin as the ruby.
 * The LEGO decomposition is used only to decide which word tiles are salient.
 *
 * Returns null when the native phrase can't be aligned to the pinyin words
 * (rare data defects) so the caller can fall back.
 */
export function buildWordTiles(
  romanPhrase: string,
  nativePhrase: string,
  opts: WordTileOptions = {},
): LegoBlock[] | null {
  const words = romanPhrase.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return null
  const natives = sliceNativeByRoman(words, nativePhrase)
  if (!natives) return null

  // Only salience matching needs a concatenated sentence. Walk offsets while
  // emitting tiles instead of allocating an offset pair for every word.
  const concat = opts.salientNativeTexts?.length ? natives.join('') : ''

  const salientRanges: Array<[number, number]> = []
  for (const s of opts.salientNativeTexts || []) {
    if (!s) continue
    const idx = concat.indexOf(s)
    if (idx >= 0) salientRanges.push([idx, idx + s.length])
  }
  const anySalient = salientRanges.length > 0
  const overlapsSalient = (a: number, b: number) =>
    salientRanges.some(([s, e]) => a < e && b > s)

  const prefix = opts.idPrefix || 'w'
  let pos = 0
  return words.map((w, i) => {
    const a = pos
    pos += natives[i].length
    return {
      id: `${prefix}_${i}`,
      targetText: natives[i] || w,
      romanText: w,
      // No salient info → emphasise everything (never fade the whole phrase).
      isSalient: anySalient ? overlapsSalient(a, pos) : true,
    }
  })
}

const PUNCT_STRIP = /[.,!?;:¡¿"“”„«»。、！？；：؟،…]+/gu
function normWord(w: string): string {
  return w.toLowerCase().replace(PUNCT_STRIP, '').trim()
}

export interface WordPairOptions {
  /** Native-script text(s) of the salient LEGO(s) — word tiles inside one of
   *  these get isSalient=true. None matched → every tile is salient. */
  salientNativeTexts?: string[]
  idPrefix?: string
}

/**
 * Tile a phrase by WORD for space-separated scripts (Korean, Russian, Greek,
 * Armenian, Arabic, Hindi, Ukrainian, Persian, Bulgarian, Hebrew).
 *
 * Native and romanisation are both whitespace-segmented and map one-for-one, so
 * we zip them into one tile per word: native word as the primary glyph, the
 * matching romanised word as the ruby. This goes FINER than the LEGO
 * decomposition (whose salient can be a whole clause) — the romanisation's word
 * spacing is the parseable unit. Salient tiles are marked from the salient
 * LEGO's native text.
 *
 * Returns null when the word counts don't line up (rare romanisations that join
 * two words) so the caller can fall back.
 */
export function buildWordPairTiles(
  romanPhrase: string,
  nativePhrase: string,
  opts: WordPairOptions = {},
): LegoBlock[] | null {
  const nativeWords = nativePhrase.trim().split(/\s+/).filter(Boolean)
  const romanWords = romanPhrase.trim().split(/\s+/).filter(Boolean)
  if (nativeWords.length === 0 || nativeWords.length !== romanWords.length) return null

  // Normalized words are only needed for salient matching. Plain tile renders
  // can zip the original words without lowercasing and stripping each one.
  const normNative = opts.salientNativeTexts?.length ? nativeWords.map(normWord) : []
  const salient = new Set<number>()
  for (const s of opts.salientNativeTexts || []) {
    // Later matches cannot change a fully salient phrase. Avoid normalizing
    // and searching the remaining texts once every word is already marked.
    if (salient.size === nativeWords.length) break
    // A whole-phrase salient has the same words we already normalized above.
    const sw = (s === nativePhrase ? normNative : s.trim().split(/\s+/).map(normWord)).filter(Boolean)
    if (sw.length === 0) continue
    for (let i = 0; i + sw.length <= normNative.length; i++) {
      let match = true
      for (let k = 0; k < sw.length; k++) {
        if (normNative[i + k] !== sw[k]) { match = false; break }
      }
      if (match) for (let k = 0; k < sw.length; k++) salient.add(i + k)
    }
  }
  const anySalient = salient.size > 0
  const prefix = opts.idPrefix || 'w'
  return nativeWords.map((nw, i) => ({
    id: `${prefix}_${i}`,
    targetText: nw,
    romanText: romanWords[i],
    isSalient: anySalient ? salient.has(i) : true,
  }))
}

// Hiragana → romaji candidates (Hepburn + common variants). Used only to ANCHOR
// the phrase's own romanisation to the device-segmented native words — the romaji
// text shown comes from the phrase itself, so this just needs to find match
// positions. Katakana folds onto hiragana (code - 0x60). Kanji, ー and small っ
// are treated as gaps whose romaji is recovered from between anchors.
const HIRA: Record<string, string[]> = {
  あ:['a'],い:['i'],う:['u'],え:['e'],お:['o'],
  か:['ka'],き:['ki'],く:['ku'],け:['ke'],こ:['ko'],
  が:['ga'],ぎ:['gi'],ぐ:['gu'],げ:['ge'],ご:['go'],
  さ:['sa'],し:['shi','si'],す:['su'],せ:['se'],そ:['so'],
  ざ:['za'],じ:['ji','zi'],ず:['zu'],ぜ:['ze'],ぞ:['zo'],
  た:['ta'],ち:['chi','ti'],つ:['tsu','tu'],て:['te'],と:['to'],
  だ:['da'],ぢ:['ji','di'],づ:['zu','du'],で:['de'],ど:['do'],
  な:['na'],に:['ni'],ぬ:['nu'],ね:['ne'],の:['no'],
  は:['ha','wa'],ひ:['hi'],ふ:['fu','hu'],へ:['he','e'],ほ:['ho'],
  ば:['ba'],び:['bi'],ぶ:['bu'],べ:['be'],ぼ:['bo'],
  ぱ:['pa'],ぴ:['pi'],ぷ:['pu'],ぺ:['pe'],ぽ:['po'],
  ま:['ma'],み:['mi'],む:['mu'],め:['me'],も:['mo'],
  や:['ya'],ゆ:['yu'],よ:['yo'],
  ら:['ra'],り:['ri'],る:['ru'],れ:['re'],ろ:['ro'],
  わ:['wa'],を:['o','wo'],ん:['n','m'],ゔ:['vu'],
}
const YOUON: Record<string, string[]> = {
  きゃ:['kya'],きゅ:['kyu'],きょ:['kyo'],ぎゃ:['gya'],ぎゅ:['gyu'],ぎょ:['gyo'],
  しゃ:['sha','sya'],しゅ:['shu','syu'],しょ:['sho','syo'],じゃ:['ja','zya'],じゅ:['ju','zyu'],じょ:['jo','zyo'],
  ちゃ:['cha','tya'],ちゅ:['chu','tyu'],ちょ:['cho','tyo'],
  にゃ:['nya'],にゅ:['nyu'],にょ:['nyo'],ひゃ:['hya'],ひゅ:['hyu'],ひょ:['hyo'],
  びゃ:['bya'],びゅ:['byu'],びょ:['byo'],ぴゃ:['pya'],ぴゅ:['pyu'],ぴょ:['pyo'],
  みゃ:['mya'],みゅ:['myu'],みょ:['myo'],りゃ:['rya'],りゅ:['ryu'],りょ:['ryo'],
}
function toHira(ch: string): string {
  const c = ch.charCodeAt(0)
  return c >= 0x30a1 && c <= 0x30f6 ? String.fromCharCode(c - 0x60) : ch
}
/** Romaji candidates for the kana unit at index i (handles 2-char youon), or
 *  null for kanji / ー / small っ — which become gaps filled from the stream. */
function kanaUnit(chars: string[], i: number): { cands: string[]; len: number } | null {
  const a = toHira(chars[i])
  const b = i + 1 < chars.length ? toHira(chars[i + 1]) : ''
  if (b && (a === 'ゃ' || a === 'ゅ' || a === 'ょ')) return null
  if (b && YOUON[a + b]) return { cands: YOUON[a + b], len: 2 }
  if (HIRA[a]) return { cands: HIRA[a], len: 1 }
  return null
}

/**
 * Forced-align a Japanese phrase's romanisation onto device-segmented native
 * words. Kana are anchors (their romaji is found in the phrase romaji); kanji,
 * ー and っ are gaps whose romaji is whatever sits between anchors. Returns one
 * romaji string per native word, or null if any word couldn't be filled.
 *
 * e.g. ['止め','たい','と','思う'] + 'yametai to omou' → ['yame','tai','to','omou']
 *      ('止め'='yame' — the kanji reading is recovered from the stream gap.)
 */
export function alignJapaneseRomaji(
  nativeWords: string[],
  phraseRomaji: string,
  dict?: Map<string, string>,
): string[] | null {
  const stream = (phraseRomaji || '').toLowerCase().replace(/[^a-z]/g, '')
  if (!stream || nativeWords.length === 0) return null
  const out = nativeWords.map(() => '')
  // Repeated words share a reading within this sentence. Keep the cache local
  // so course dictionary corrections are visible on the next tile rebuild.
  const readings = new Map<string, string>()
  let pos = 0
  let pendingWord = -1 // word owning the kanji/gap chars not yet attributed
  // No anchor or dictionary reading can match past the end of the stream.
  // Leave the remaining readings empty for the caller's normal fallback.
  for (let wi = 0; wi < nativeWords.length && pos < stream.length; wi++) {
    // Dictionary-consume first: if this word's known reading matches the stream
    // here, take it. This advances the pointer past kanji readings so a short
    // following kana (o / a / i) can't false-anchor *inside* them — e.g. を "o"
    // matching the "o" in 今週 "k(o)nshuu".
    if (pendingWord < 0) {
      const word = nativeWords[wi]
      let d = readings.get(word)
      if (d === undefined) {
        d = dict?.get(word)?.toLowerCase().replace(/[^a-z]/g, '') || ''
        readings.set(word, d)
      }
      if (d && stream.startsWith(d, pos)) {
        out[wi] = stream.slice(pos, pos + d.length)
        pos += d.length
        continue
      }
    }
    const chars = [...nativeWords[wi]]
    for (let ci = 0; ci < chars.length && pos < stream.length; ) {
      const unit = kanaUnit(chars, ci)
      if (!unit) { if (pendingWord < 0) pendingWord = wi; ci += 1; continue }
      let at = -1, len = 0
      for (const cand of unit.cands) {
        const idx = stream.indexOf(cand, pos)
        if (idx >= 0 && (at < 0 || idx < at)) { at = idx; len = cand.length }
        // No later variant can beat a match at the cursor; ties keep the
        // first candidate, just as they do in the full search.
        if (at === pos) break
      }
      if (at < 0) { if (pendingWord < 0) pendingWord = wi; ci += unit.len; continue }
      if (at > pos) out[pendingWord >= 0 ? pendingWord : wi] += stream.slice(pos, at)
      out[wi] += stream.slice(at, at + len)
      pos = at + len
      pendingWord = -1
      ci += unit.len
    }
  }
  if (pos === 0) return null // nothing anchored (e.g. no kana at all)
  if (pos < stream.length) out[pendingWord >= 0 ? pendingWord : out.length - 1] += stream.slice(pos)
  // Partial result is fine: words the aligner couldn't anchor come back empty and
  // the caller fills them from the dictionary. Anchored words are always correct
  // (positions are found absolutely), so empties never mean WRONG romaji.
  return out
}

export interface SegmentedTileOptions extends WordPairOptions {
  /** native word → romanisation, from the course's own legos/atoms — used to
   *  fill the ruby when the phrase's romanisation doesn't split 1:1 with the
   *  device's word segmentation. */
  nativeRomajiDict?: Map<string, string>
}

// Phrase renders within a course share a locale. Keep only the most recent
// segmenter so switching courses cannot grow the cache indefinitely.
let cachedWordSegmenter: {
  locale: string
  segmenter: { segment(input: string): Iterable<{ isWordLike?: boolean; segment: string }> }
  lastPhrase?: string
  lastWords?: string[]
} | undefined

/**
 * Tile a spaceless script (Japanese, Thai) by asking the DEVICE to segment the
 * native sentence into words — `Intl.Segmenter(locale, {granularity:'word'})`
 * uses the platform's built-in ICU dictionaries (Japanese/Thai/Chinese), so we
 * get genuinely parseable native word tiles with NO dependency on the (sparse,
 * ghost-heavy) backend decomposition.
 *
 * Romaji ruby per tile:
 *  - if the phrase's romanisation splits into the same number of words, pair
 *    positionally (uses the actual romanisation, inflection-correct);
 *  - otherwise look each native word up in nativeRomajiDict (the course's
 *    lego/atom native→romaji pairs). Words in neither just show without a ruby
 *    (still native, still parseable).
 *
 * Returns null when Intl.Segmenter is unavailable (old engines) so the caller
 * can fall back.
 */
export function buildSegmentedTiles(
  nativePhrase: string,
  romanPhrase: string,
  locale: string,
  opts: SegmentedTileOptions = {},
): LegoBlock[] | null {
  const Seg = (Intl as any)?.Segmenter
  if (!Seg || !nativePhrase) return null
  let nativeWords: string[]
  try {
    if (cachedWordSegmenter?.locale !== locale) {
      const segmenter = new Seg(locale, { granularity: 'word' })
      cachedWordSegmenter = { locale, segmenter }
    }
    const seg = cachedWordSegmenter.segmenter
    // Keep only the last sentence: tile rebuilds can change ruby, salience or
    // ids without changing the native words. Never cache the mutable tiles.
    if (cachedWordSegmenter.lastPhrase === nativePhrase && cachedWordSegmenter.lastWords) {
      nativeWords = cachedWordSegmenter.lastWords
    } else {
      nativeWords = [...seg.segment(nativePhrase)]
        .filter((s: any) => s.isWordLike)
        .map((s: any) => s.segment as string)
      cachedWordSegmenter.lastPhrase = nativePhrase
      cachedWordSegmenter.lastWords = nativeWords
    }
  } catch {
    return null
  }
  if (nativeWords.length === 0) return null

  // Positional words are only a fallback: Japanese alignment often supplies
  // every reading, so defer splitting until a tile actually needs it.
  let romanWords: string[] | undefined
  const fallbackReading = (i: number, word: string): string => {
    romanWords ??= romanPhrase.trim().split(/\s+/).filter(Boolean)
    return romanWords.length === nativeWords.length ? romanWords[i] : (dict?.get(word) || '')
  }
  const dict = opts.nativeRomajiDict
  // Japanese: forced-align the phrase romaji onto the segmented words (kana
  // anchors + kanji gaps), so every tile gets its reading even when the romaji's
  // word boundaries differ from the device's segmentation.
  const aligned = locale.startsWith('ja') ? alignJapaneseRomaji(nativeWords, romanPhrase, dict) : null

  // Salient native-word indices: the salient LEGO text is unsegmented, so locate
  // it by character offset within the concatenated native words.
  // Plain renders need no character-offset search or concatenated sentence.
  const concat = opts.salientNativeTexts?.length ? nativeWords.join('') : ''
  const salientRanges: Array<[number, number]> = []
  for (const s of opts.salientNativeTexts || []) {
    if (!s) continue
    const normalized = s.replace(/\s+/g, '')
    const idx = concat.indexOf(normalized)
    if (idx >= 0) salientRanges.push([idx, idx + normalized.length])
  }
  const anySalient = salientRanges.length > 0
  const overlaps = (a: number, b: number) => salientRanges.some(([s, e]) => a < e && b > s)

  const prefix = opts.idPrefix || 'w'
  // Walk offsets while emitting tiles, avoiding a separate pair per word on
  // every learner render (including renders without any salient text).
  let pos = 0
  return nativeWords.map((nw, i) => {
    const roman = (aligned && aligned[i]) || fallbackReading(i, nw)
    const a = pos
    pos += nw.length
    return {
      id: `${prefix}_${i}`,
      targetText: nw,
      ...(roman ? { romanText: roman } : {}),
      isSalient: anySalient ? overlaps(a, pos) : true,
    }
  })
}

/**
 * Pair native text onto an existing ROMANISED LEGO tiling, for scripts where the
 * native side can't be word-split or sliced (Japanese, Thai). The romanisation
 * decomposes cleanly into LEGO tiles; for each tile we swap in the LEGO's native
 * text (by legoId) as the primary glyph and keep the romanisation as the ruby.
 * Tiles whose id isn't a known LEGO (ghost tokens) stay romanised with no ruby —
 * better than the ghost-character native tiling the backend produces here.
 */
export function nativeFromRomanTiles(
  romanBlocks: LegoBlock[],
  nativeById: Map<string, string>,
): LegoBlock[] {
  // With no native entries every tile stays romanised. Avoid walking the
  // tiles and allocating an identical array in this fallback render path.
  if (romanBlocks.length === 0 || nativeById.size === 0) return romanBlocks
  let out: LegoBlock[] | undefined
  for (let i = 0; i < romanBlocks.length; i++) {
    const b = romanBlocks[i]
    const native = nativeById.get(b.id)
    if (native) {
      // Ghost IDs can miss even a populated dictionary. Copy only when a
      // tile changes, retaining the input array for an unchanged render.
      out ??= romanBlocks.slice()
      out[i] = { ...b, targetText: native, romanText: b.targetText }
    }
  }
  return out ?? romanBlocks
}

/**
 * Promote a romanised-script tiling to dual-script tiles.
 *
 * The tiling is computed once on the romanised script (which segments cleanly);
 * this turns each tile into native-primary form — `targetText` becomes the
 * native-script slice, and the romanised text rides along as `romanText` for the
 * optional ruby annotation. M-LEGO components are aligned to native too so a
 * tile's internal breakdown stays in the same script as the tile.
 *
 * The tile BOUNDARIES are preserved exactly — only the script of the text
 * changes. If the native phrase can't be aligned to the tiles (rare data
 * anomalies: a missing apostrophe, a pinyin/hanzi content mismatch), the
 * romanised tiling is returned unchanged so the learner still sees correct
 * chunking rather than a mis-sliced native render.
 */
export function applyDualScript(blocks: LegoBlock[], nativePhrase: string): LegoBlock[] {
  if (blocks.length === 0) return blocks
  const nativeSlices = sliceNativeByRoman(blocks.map(b => b.targetText), nativePhrase)
  if (!nativeSlices) return blocks

  return blocks.map((b, i) => {
    const nativeText = nativeSlices[i] || b.targetText
    const out: LegoBlock = { ...b, targetText: nativeText, romanText: b.targetText }
    if (b.components && b.components.length > 0) {
      const compNatives = sliceNativeByRoman(b.components.map(c => c.target), nativeText)
      out.components = compNatives
        ? b.components.map((c, ci) => ({ ...c, target: compNatives[ci] || c.target }))
        : undefined // alignment failed → render as a single native span
    }
    return out
  })
}
